import {useState} from 'react';
import {useReactiveVar} from '@apollo/client';
import {Content, DialogClose, Portal} from '@radix-ui/react-dialog';
import cx from 'classnames';

import {surveyVar} from '#apollo/local/SurveyVar';
import {Button, Text} from '#component/base';
import * as modalCss from '#component/base/Modal/Modal.css';
import {IntroStep} from '#component/page/Survey/component/IntroStep';
import {FullscreenModalHeader} from '#component/partial';
import {EventType, PageViewEvent, useAnalytics} from '#component/provider/AnalyticsProvider';
import {useOnceEffect} from '#hook';

import {CONTINUE_STEP, INTRO_STEPS} from './IntroModal.util';

import * as css from './IntroModal.css';

export const IntroModal = ({surveyId = ''}) => {
  const [step, setStep] = useState(0);
  const {track} = useAnalytics();
  const savedSurveys = useReactiveVar(surveyVar);
  const isResuming = !!savedSurveys[surveyId || ''];
  const {ctaProps, showSkip} = isResuming ? CONTINUE_STEP : INTRO_STEPS[step];

  const handleCtaClick = () => {
    const nextStep = step + 1;

    if (nextStep < INTRO_STEPS.length) {
      setStep(nextStep);
      track<PageViewEvent>({evtName: EventType.PageView, pageName: `Survey Intro ${nextStep + 1}`});
    }
  };

  useOnceEffect(() => {
    // Calling this event on mount is intentional — the Intro Modal begins
    // open by default whenever the user visits the `/survey` route.
    track<PageViewEvent>({
      evtName: EventType.PageView,
      pageName: isResuming ? 'Survey Continue' : 'Survey Intro 1',
    });
  });

  return (
    <Portal>
      <div className={cx(modalCss.dialogRoot({layout: 'fullScreen'}))}>
        <Content
          className={cx(
            modalCss.dialogContent({layout: 'fullScreen', variant: 'dark'}),
            css.dialogContent,
          )}>
          <div className={css.contentWrapper}>
            <FullscreenModalHeader
              className={css.header}
              buttonVariant="secondary-dark"
              logoColor="cotton"
            />

            <main className={css.step}>
              {isResuming ? (
                <IntroStep {...CONTINUE_STEP} show />
              ) : (
                INTRO_STEPS.map(({ctaProps, showSkip, ...stepProps}, i) => {
                  const shouldMount = i === step || i === step + 1;

                  return shouldMount ? (
                    <IntroStep {...stepProps} show={i === step} key={i} />
                  ) : null;
                })
              )}
            </main>

            <footer className={css.footer}>
              <Button variant="primary-dark" {...ctaProps} onClick={handleCtaClick} />

              {showSkip && (
                <Text as={DialogClose} color="cotton">
                  Skip Intro
                </Text>
              )}
            </footer>
          </div>
        </Content>
      </div>
    </Portal>
  );
};
